import { Link } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import type { LegalOperator, LegalPage as LegalPageType } from "@/lib/legal.functions";

function substituteOperator(text: string, op: LegalOperator): string {
  return text
    .replace(/\{\{\s*operator\.name\s*\}\}/g, op.name)
    .replace(/\{\{\s*operator\.address\s*\}\}/g, op.address)
    .replace(/\{\{\s*operator\.businessId\s*\}\}/g, op.businessId)
    .replace(/\{\{\s*operator\.contactEmail\s*\}\}/g, op.contactEmail);
}

export function LegalPageView({
  page,
  operator,
}: {
  page: LegalPageType;
  operator: LegalOperator;
}) {
  return (
    <main className="container mx-auto max-w-3xl px-4 sm:px-6 py-16 sm:py-24">
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition mb-8"
      >
        <ArrowLeft className="h-4 w-4" />
        Volver al inicio
      </Link>

      <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">
        {page.title}
      </h1>
      {page.updated && (
        <p className="mt-2 text-muted-foreground text-sm">
          Última actualización: {page.updated}
        </p>
      )}

      <div className="mt-10 space-y-10 text-foreground/90 leading-relaxed">
        {page.sections.map((s, i) => (
          <section key={i}>
            {s.heading && (
              <h2 className="font-semibold text-lg mb-3">{s.heading}</h2>
            )}
            {s.body.map((p, j) => (
              <p key={j} className="mt-2 whitespace-pre-line">
                {substituteOperator(p, operator)}
              </p>
            ))}
          </section>
        ))}

        <section className="border-t border-border pt-6 text-sm text-muted-foreground">
          <p>
            <strong className="text-foreground">{operator.name}</strong>
            {operator.businessId ? ` · ${operator.businessId}` : ""}
          </p>
          {operator.address && (
            <p className="mt-1 whitespace-pre-line">{operator.address}</p>
          )}
          {operator.contactEmail && (
            <p className="mt-1">
              <a
                href={`mailto:${operator.contactEmail}`}
                className="text-primary hover:underline"
              >
                {operator.contactEmail}
              </a>
            </p>
          )}
        </section>
      </div>
    </main>
  );
}

import {
  SITE_ID,
  SITE_URL,
  WEBSITE_ID,
  plainText,
  safeJsonLd,
} from "@/lib/jsonld";

type LegalKind = "cookies" | "privacy" | "terms";

const PATH_TO_KIND: Record<string, LegalKind> = {
  "/cookies": "cookies",
  "/privacidad": "privacy",
  "/terminos": "terms",
};

function policyNode(
  page: LegalPageType,
  url: string,
  kind: LegalKind,
  lang: string,
) {
  const type =
    kind === "privacy"
      ? "PrivacyPolicy"
      : kind === "terms"
      ? "TermsOfService"
      : "CreativeWork";
  return {
    "@type": type,
    "@id": `${url}#policy`,
    name: plainText(page.title),
    url,
    inLanguage: lang,
    dateModified: page.updated || undefined,
    publisher: { "@id": SITE_ID },
    ...(kind === "cookies" ? { genre: "Cookie Policy" } : {}),
  };
}

export function legalMeta(
  page: LegalPageType,
  path: "/cookies" | "/privacidad" | "/terminos",
) {
  const firstBody = page.sections?.[0]?.body?.[0] ?? "";
  const description = plainText(firstBody).slice(0, 155);
  const url = `${SITE_URL}${path}`;
  const lang = "es";
  const kind = PATH_TO_KIND[path];
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${url}#webpage`,
        url,
        name: plainText(page.title),
        inLanguage: lang,
        isPartOf: { "@id": WEBSITE_ID },
        about: { "@id": SITE_ID },
        publisher: { "@id": SITE_ID },
        dateModified: page.updated || undefined,
        mainEntity: { "@id": `${url}#policy` },
      },
      policyNode(page, url, kind, lang),
    ],
  };
  return {
    meta: [
      { title: `${page.title} — Bizaclia.com` },
      { name: "description", content: description },
      { name: "robots", content: "index, follow" },
      { property: "og:title", content: `${page.title} — Bizaclia.com` },
      { property: "og:description", content: description },
      { property: "og:url", content: url },
    ],
    links: [{ rel: "canonical", href: url }],
    scripts: [
      {
        type: "application/ld+json",
        children: safeJsonLd(jsonLd),
      },
    ],
  };
}
